import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';
import type { Database } from '../../types/database.types';
import type { ParsedSprintRow } from '../../utils/csvParsing';

type SprintRow = Database['public']['Tables']['sprints']['Row'];

export const sprintsApi = {
  async getAll(): Promise<{ data: SprintRow[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('sprints')
        .select('*')
        .order('sprint_number', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  },

  /**
   * Replaces all sprints for the current user with the imported set.
   * Uses a compensating-transaction pattern: backs up existing sprints before
   * deleting, then restores them if the insert fails. This avoids data loss
   * when Supabase JS v2 does not support true transactions.
   */
  async importSprints(
    sprints: ParsedSprintRow[],
    userId: string
  ): Promise<{ data: SprintRow[] | null; error: Error | null }> {
    try {
      // Backup existing sprints so we can restore on insert failure
      const { data: backup } = await supabase
        .from('sprints')
        .select('sprint_number, name, start_date, end_date')
        .eq('user_id', userId);

      const { error: deleteError } = await supabase
        .from('sprints')
        .delete()
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      const rows = sprints.map((s) => ({
        user_id: userId,
        sprint_number: s.sprint_number,
        name: s.name,
        start_date: format(s.start_date, 'yyyy-MM-dd'),
        end_date: format(s.end_date, 'yyyy-MM-dd'),
      }));

      const { data, error: insertError } = await supabase
        .from('sprints')
        .insert(rows)
        .select();

      if (insertError) {
        // Attempt to restore backed-up sprints to avoid leaving user with empty calendar
        if (backup && backup.length > 0) {
          await supabase
            .from('sprints')
            .insert(backup.map((r) => ({ user_id: userId, ...r })));
        }
        throw insertError;
      }

      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  },
};
